import { gateway } from "../../../gateway";

/** Axios body is { status, message, data }; pagination lives in `.data`. */
const unwrapApiData = (res) => res?.data?.data ?? res?.data;

export const normalizeJobChatMessage = (message) => {
  if (!message) return message;
  const sender = message.sender || {};
  const fullName = sender.name || "";
  const [firstName, ...rest] = fullName.split(" ");
  const content = message.content || {};
  const category = content.category === "text" ? "plainText" : content.category;

  return {
    ...message,
    sender: {
      ...sender,
      firstName: sender.firstName ?? firstName ?? "",
      lastName: sender.lastName ?? rest.join(" ") ?? "",
      avatar: sender.avatar ?? sender.avatarUrl ?? null,
    },
    content: {
      ...content,
      category,
    },
  };
};

export const getActiveJobChats = async (token, { page = 1, pageSize = 50 } = {}) => {
  const params = new URLSearchParams();
  params.append("page", String(page));
  params.append("pageSize", String(pageSize));

  const res = await gateway.get(`/job-chats/active?${params.toString()}`, {
    headers: { Authorization: `${token}` },
  });
  return unwrapApiData(res);
};

export const getJobChatHistory = async (token, { page = 1, pageSize = 50 } = {}) => {
  const params = new URLSearchParams();
  params.append("page", String(page));
  params.append("pageSize", String(pageSize));

  const res = await gateway.get(`/job-chats/history?${params.toString()}`, {
    headers: { Authorization: `${token}` },
  });
  return unwrapApiData(res);
};

const buildCompanyHeaders = (companyId) => (companyId != null ? { "x-company-id": String(companyId) } : {});

export const getJobChatSummary = async (token, jobEventUid, companyId = undefined) => {
  const res = await gateway.get(`/job-chats/${jobEventUid}`, {
    headers: { Authorization: `${token}`, ...buildCompanyHeaders(companyId) },
  });
  return unwrapApiData(res);
};

export const getJobChatMessages = async (token, jobEventUid, { page = 1, pageSize = 20, order = "DESC", companyId = undefined } = {}) => {
  const params = new URLSearchParams();
  params.append("page", String(page));
  params.append("pageSize", String(pageSize));
  params.append("order", order);

  const res = await gateway.get(`/job-chats/${jobEventUid}/messages?${params.toString()}`, {
    headers: { Authorization: `${token}`, ...buildCompanyHeaders(companyId) },
  });

  const data = unwrapApiData(res);
  return {
    ...data,
    items: (data?.items || []).map(normalizeJobChatMessage),
  };
};

export const markJobChatRead = async (token, jobEventUid, companyId = undefined) => {
  const res = await gateway.post(`/job-chats/${jobEventUid}/read`, null, {
    headers: { Authorization: `${token}`, ...buildCompanyHeaders(companyId) },
  });
  return unwrapApiData(res);
};

/**
 * Pure cache helpers — used by both the mutation and the socket so realtime updates
 * patch the React Query cache in place instead of refetching the whole paginated list.
 */
export const upsertMessageInPages = (oldData, message) => {
  if (!message) return oldData;
  if (!oldData || !Array.isArray(oldData.pages)) {
    return { pages: [{ items: [message] }], pageParams: [1] };
  }
  const exists = oldData.pages.some((page) => (page?.items || []).some((m) => m.uid === message.uid));
  if (exists) return oldData;

  const pages = [...oldData.pages];
  const first = pages[0] || { items: [] };
  pages[0] = { ...first, items: [message, ...(first.items || [])] };
  return { ...oldData, pages };
};

export const bumpJobChatListItem = (
  oldData,
  jobEventUid,
  options: { lastMessage?: string | null; lastMessageAt?: string | null; incrementUnread?: boolean } = {}
) => {
  const { lastMessage, lastMessageAt, incrementUnread } = options;
  if (!oldData || !Array.isArray(oldData.items)) return oldData;
  const idx = oldData.items.findIndex((c) => c.jobEventUid === jobEventUid);
  if (idx === -1) return oldData;

  const item = oldData.items[idx];
  const updated = {
    ...item,
    lastMessage: lastMessage ?? item.lastMessage,
    lastMessageAt: lastMessageAt ?? item.lastMessageAt,
    unreadCount: incrementUnread ? (item.unreadCount || 0) + 1 : item.unreadCount,
  };

  return { ...oldData, items: [updated, ...oldData.items.slice(0, idx), ...oldData.items.slice(idx + 1)] };
};

export const clearJobChatUnread = (oldData, jobEventUid) => {
  if (!oldData || !Array.isArray(oldData.items)) return oldData;
  let changed = false;
  const items = oldData.items.map((c) => {
    if (c.jobEventUid === jobEventUid && c.unreadCount) {
      changed = true;
      return { ...c, unreadCount: 0 };
    }
    return c;
  });
  return changed ? { ...oldData, items } : oldData;
};

export const sendJobChatMessage = async (token, jobEventUid, messageData, companyId = undefined) => {
  const formData = new FormData();
  formData.append("name", messageData.name || "Text");

  if (messageData.plainText) {
    formData.append("plainText", messageData.plainText);
  }
  if (messageData.file) {
    formData.append("file", messageData.file);
  }

  const res = await gateway.post(`/job-chats/${jobEventUid}/messages`, formData, {
    headers: {
      Authorization: `${token}`,
      ...buildCompanyHeaders(companyId),
      "Content-Type": "multipart/form-data",
    },
  });

  return unwrapApiData(res);
};
